/**
 * PasteTextPanel — paste-as-document surface for Stage 0.
 *
 * A collapsible companion to <UploadZone> that lets a drafter paste
 * an email thread, an inventor call note, or any free-text snippet
 * directly into the matter. Submitted content fans out into the same
 * upload pipeline UploadZone uses:
 *   1. The text body is wrapped in a synthetic ``.txt`` File and
 *      POSTed through ``api.uploadIdf`` — the existing async/sync
 *      pipeline runs end-to-end (classifier, tree, char_count,
 *      indexed_at all light up exactly as for a real .txt upload).
 *   2. Images extracted from clipboard payloads (the user pasted a
 *      screenshot, or pasted formatted content with embedded
 *      pictures) get one extra POST each as standalone-image
 *      ``supplementary`` documents — file_parser.extract_file_full's
 *      standalone-image branch already handles these end-to-end.
 *
 * Zero backend changes: every upload routes through
 * ``POST /api/matter/{id}/idf`` exactly like the dropzone path.
 *
 * The contentEditable surface follows the convention established by
 * spec/ParagraphBlock.tsx (no editor library — raw DOM with onPaste
 * + onInput + suppressContentEditableWarning). Pasted HTML is
 * intentionally stripped on the way in: ``getData('text/plain')``
 * gives the cleaned text, which is inserted at the caret. Nothing
 * persists HTML, so DOMPurify isn't needed.
 *
 * Vision mode persistence mirrors UploadZone — both read/write
 * localStorage["stage1.visionMode"] so the drafter's choice carries
 * across both surfaces without lifting state into Stage0.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import {
  PenLine, ChevronDown, X, Loader2,
} from 'lucide-react';

import * as api from '@/api/client';


type VisionMode = 'auto' | 'on' | 'off';

interface PasteTextPanelProps {
  matterId: string;
  /** Same callback shape UploadZone fires — bumps refresh tokens
   *  in Stage0 so UploadedFilesList refetches without waiting for
   *  its 5s poll. */
  onUploaded: () => void;
}

interface PastedImage {
  id: string;
  blob: Blob;
  objectUrl: string;
  filename: string;
}


function genId(): string {
  // Match UploadZone's existing fallback for browsers without
  // crypto.randomUUID (older Safari) so behaviour is consistent.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}


function readPersistedVisionMode(): VisionMode {
  // Same key UploadZone writes to. Both surfaces share the
  // preference; flipping it in either persists.
  try {
    const v = localStorage.getItem('stage1.visionMode');
    if (v === 'auto' || v === 'on' || v === 'off') return v;
  } catch { /* localStorage unavailable */ }
  return 'off';
}


function sanitiseLabelForFilename(label: string): string {
  // Strip path separators + control chars; trim length so
  // server-side filename handling doesn't have to. Matches the
  // tolerance the upload endpoint already applies on its side.
  return label
    .replace(/[\\/\x00-\x1f\x7f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40);
}


export default function PasteTextPanel({
  matterId, onUploaded,
}: PasteTextPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pastedImages, setPastedImages] = useState<PastedImage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  // Object-URL leak guard. Track the live set in a ref so the
  // unmount cleanup sweeps every URL we've created (not just the
  // ones still in state at unmount time — e.g. if React batches
  // setPastedImages writes mid-paste). Each enqueue / dequeue
  // updates both the ref and state in lock-step.
  const liveUrlsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const urls = liveUrlsRef.current;
    return () => {
      urls.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* tolerate */ }
      });
      urls.clear();
    };
  }, []);

  const updateCharCount = useCallback(() => {
    const text = editorRef.current?.innerText ?? '';
    setCharCount(text.trim().length);
  }, []);

  const handleInput = useCallback(() => {
    updateCharCount();
  }, [updateCharCount]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const cd = e.clipboardData;

    // 1. Plain text inserted at the caret. ``getData('text/plain')``
    //    is the safe channel — it's already stripped of HTML
    //    formatting by the OS clipboard. Insert via Range so we
    //    preserve the existing editor content + caret position.
    const text = cd.getData('text/plain');
    if (text) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const node = document.createTextNode(text);
        range.insertNode(node);
        // Move caret to the end of the inserted text so successive
        // pastes / typing land after it rather than re-overwriting.
        range.setStartAfter(node);
        range.setEndAfter(node);
        sel.removeAllRanges();
        sel.addRange(range);
      } else if (editorRef.current) {
        editorRef.current.appendChild(document.createTextNode(text));
      }
    }

    // 2. Images on the clipboard ride alongside text. Iterate every
    //    item; ``kind === 'file'`` items expose getAsFile() (returns
    //    a Blob/File). Both raw screenshot pastes (image/png) and
    //    pasted HTML with inline images (browsers attach the inline
    //    images as separate items) land here.
    const items = Array.from(cd.items || []);
    const newImages: PastedImage[] = [];
    for (const item of items) {
      if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
      const blob = item.getAsFile();
      if (!blob) continue;
      const id = genId();
      const objectUrl = URL.createObjectURL(blob);
      liveUrlsRef.current.add(objectUrl);
      const ext = (item.type.split('/')[1] || 'png').toLowerCase();
      newImages.push({
        id,
        blob,
        objectUrl,
        filename: `pasted-img-${id.slice(0, 8)}.${ext}`,
      });
    }
    if (newImages.length > 0) {
      setPastedImages((prev) => [...prev, ...newImages]);
    }

    // 3. Refresh the char-count badge after the synchronous DOM
    //    write above. innerText is computed on read so this is
    //    cheap.
    updateCharCount();
  }, [updateCharCount]);

  const removeImage = useCallback((id: string) => {
    setPastedImages((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target) {
        try { URL.revokeObjectURL(target.objectUrl); } catch { /* tolerate */ }
        liveUrlsRef.current.delete(target.objectUrl);
      }
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const handleClear = useCallback(() => {
    if (editorRef.current) editorRef.current.innerText = '';
    setPastedImages((prev) => {
      prev.forEach((img) => {
        try { URL.revokeObjectURL(img.objectUrl); } catch { /* tolerate */ }
        liveUrlsRef.current.delete(img.objectUrl);
      });
      return [];
    });
    if (titleInputRef.current) titleInputRef.current.value = '';
    setCharCount(0);
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    const text = editorRef.current?.innerText?.trim() ?? '';
    if (!text || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const visionMode = readPersistedVisionMode();
    const ts = Date.now();
    const rawLabel = titleInputRef.current?.value ?? '';
    const cleanLabel = sanitiseLabelForFilename(rawLabel);
    const filename = cleanLabel
      ? `${cleanLabel}.txt`
      : `pasted-note-${ts}.txt`;

    try {
      // 1. Text body → synthetic .txt File. file_parser's
      //    ``ext == '.txt'`` branch reads the file as plain text
      //    end-to-end with no extra parsing cost; the rest of the
      //    pipeline (matter_documents row, classifier, tree,
      //    char_count, indexed_at) lights up identically to a real
      //    .txt upload.
      const textBlob = new Blob([text], { type: 'text/plain' });
      const textFile = new File([textBlob], filename, {
        type: 'text/plain',
      });
      await api.uploadIdf(matterId, textFile, visionMode, 'auto', false);

      // 2. Each pasted image → standalone-image upload. The
      //    standalone-image branch in extract_file_full emits one
      //    matter_documents row + one matter_figures row, with
      //    optional vision captioning controlled by visionMode.
      //    Sequential to avoid hammering the upload endpoint with
      //    a multi-image paste; matches UploadZone's per-file
      //    serial pattern.
      for (const img of pastedImages) {
        const imgFile = new File([img.blob], img.filename, {
          type: img.blob.type || 'image/png',
        });
        await api.uploadIdf(
          matterId, imgFile, visionMode, 'supplementary', false,
        );
        try { URL.revokeObjectURL(img.objectUrl); } catch { /* tolerate */ }
        liveUrlsRef.current.delete(img.objectUrl);
      }

      // 3. Reset the surface. Title + body cleared, image set
      //    emptied (URLs already revoked above), panel collapsed
      //    so the dropzone reasserts as the primary surface.
      if (editorRef.current) editorRef.current.innerText = '';
      if (titleInputRef.current) titleInputRef.current.value = '';
      setPastedImages([]);
      setCharCount(0);
      setIsOpen(false);

      // 4. Tell Stage0 to bump its refresh tokens — same callback
      //    UploadZone fires — so UploadedFilesList refetches
      //    without waiting for the next 5s poll tick.
      try { onUploaded(); } catch { /* parent-callback errors are best-effort */ }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setSubmitError(
        (typeof detail === 'string' && detail)
        || detail?.message
        || err?.message
        || 'Failed to add content. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [matterId, pastedImages, isSubmitting, onUploaded]);

  const submitDisabled = isSubmitting || charCount === 0;
  const submitLabel = isSubmitting
    ? 'Adding…'
    : `Add to matter${
        pastedImages.length > 0
          ? ` (+${pastedImages.length} image${pastedImages.length === 1 ? '' : 's'})`
          : ''
      }`;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      data-testid="paste-text-panel"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid="paste-text-toggle"
          className={
            'w-full flex items-center justify-between px-4 py-3 '
            + 'border border-dashed border-zinc-300 rounded-md '
            + 'text-sm text-zinc-600 bg-white '
            + 'hover:border-[#002FA7] hover:bg-blue-50/30 hover:text-zinc-800 '
            + 'transition-colors'
          }
        >
          <span className="flex items-center gap-2">
            <PenLine size={15} />
            Or paste text, emails, or notes directly
          </span>
          <ChevronDown
            size={15}
            className={
              'transition-transform '
              + (isOpen ? 'rotate-180' : '')
            }
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div
          data-testid="paste-text-body"
          className="border border-zinc-200 rounded-md mt-2 overflow-hidden bg-white"
        >
          {/* Title — optional. When present, it sets the synthetic
              filename so the row in UploadedFilesList reads as
              "<label>.txt" instead of "pasted-note-<ts>.txt". */}
          <input
            ref={titleInputRef}
            type="text"
            data-testid="paste-text-title"
            placeholder="Label (e.g. Inventor call notes — optional)"
            className={
              'w-full bg-transparent border-b border-zinc-100 '
              + 'px-4 py-2 text-sm text-zinc-800 '
              + 'placeholder-zinc-400 outline-none '
              + 'focus:border-zinc-300'
            }
          />

          {/* ContentEditable surface. Plain-text-only by
              construction — handlePaste strips HTML on the way in
              via getData('text/plain'). innerText on submit. */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            data-testid="paste-text-editor"
            onPaste={handlePaste}
            onInput={handleInput}
            data-placeholder="Paste emails, inventor notes, meeting summaries, or any text here… Images in pasted content are extracted automatically."
            className={
              'min-h-[140px] max-h-[320px] overflow-y-auto '
              + 'px-4 py-3 text-sm text-zinc-800 leading-relaxed '
              + 'outline-none whitespace-pre-wrap '
              + 'empty:before:content-[attr(data-placeholder)] '
              + 'empty:before:text-zinc-400 '
              + 'empty:before:pointer-events-none'
            }
          />

          {/* Pasted-image thumbnails. Hover surfaces the × button
              (group-hover) so the gallery stays clean by default
              and reveals affordances when the drafter aims at a
              tile to remove it. */}
          {pastedImages.length > 0 && (
            <div
              data-testid="paste-text-images"
              className="flex flex-wrap gap-2 px-4 pb-3 border-t border-zinc-100 pt-3 items-end"
            >
              {pastedImages.map((img) => (
                <div
                  key={img.id}
                  data-testid={`paste-text-image-${img.id}`}
                  className="relative group w-16 h-16"
                >
                  <img
                    src={img.objectUrl}
                    alt="Pasted content"
                    className="w-16 h-16 object-cover rounded border border-zinc-200"
                  />
                  <button
                    type="button"
                    data-testid={`paste-text-image-remove-${img.id}`}
                    onClick={() => removeImage(img.id)}
                    aria-label="Remove image"
                    className={
                      'absolute -top-1 -right-1 '
                      + 'bg-white border border-zinc-300 rounded-full '
                      + 'w-4 h-4 text-xs text-zinc-500 '
                      + 'hidden group-hover:flex '
                      + 'items-center justify-center '
                      + 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                    }
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
              <p className="text-xs text-zinc-500 ml-auto pb-1">
                {pastedImages.length} image{pastedImages.length > 1 ? 's' : ''}{' '}
                will be uploaded as separate figures
              </p>
            </div>
          )}

          {/* Footer — char count + Clear/Add buttons. Char count
              ladder mirrors UploadedFilesList's threshold colours:
              amber at 80k, no red here because the editor itself
              caps at 320 px scroll height and a 100k-char paste
              would already be visibly enormous. */}
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-50/60 border-t border-zinc-100">
            <span
              data-testid="paste-text-charcount"
              className="text-xs text-zinc-500"
            >
              {charCount > 0 ? `${charCount.toLocaleString()} chars` : ''}
              {charCount > 80_000 && (
                <span className="text-amber-700 ml-1">
                  · large — may be truncated
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="paste-text-clear"
                onClick={handleClear}
                disabled={isSubmitting}
                className="text-xs text-zinc-500 hover:text-zinc-700 disabled:opacity-50"
              >
                Clear
              </button>
              <button
                type="button"
                data-testid="paste-text-submit"
                onClick={handleSubmit}
                disabled={submitDisabled}
                className={
                  'inline-flex items-center gap-1.5 '
                  + 'px-3 py-1.5 text-xs rounded-md '
                  + 'bg-[#002FA7] text-white '
                  + 'hover:bg-[#002277] '
                  + 'disabled:opacity-40 disabled:cursor-not-allowed'
                }
              >
                {isSubmitting && (
                  <Loader2 size={11} className="animate-spin" />
                )}
                {submitLabel}
              </button>
            </div>
          </div>

          {submitError && (
            <p
              data-testid="paste-text-error"
              className="text-xs text-red-700 px-4 pb-3 pt-1 bg-red-50 border-t border-red-100"
            >
              {submitError}
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
